import { Spacer } from '@adserve/adserve-react-components';
import React, { useState, useEffect } from 'react';
import { CampaignGoalEnum } from '../../../constants/Enums';
import { GetCampaignBursts } from '../../../services/CampaignsDataService';
import { DefaultInputStatus, goalUIStructure } from '../CampaignBrief/CampaignBriefUtils';
import { validateBurstName } from '../CampaignBrief/CampaignBriefValidationService';
import { pageContainerStyle } from '../CampaignStyles';
import CampaignBurstCommercials from './CampaignBurstCommercials';
import CampaignBurstSavePanel from './CampaignBurstSavePanel';
import CampaignBurstSummary from './CampaignBurstSummary';
import { GetAll } from '../../../services/DataService';
import PlanningGoals from '../CampaignBrief/PlanningGoals';

const CampaignBurstBrief = ({
    parentCampaign,
    loadParentCampaign,
    campaignBurst,
    changeMade,
    setValidJcn,
    validJcn,
    createCampaignBurst,
    updateCampaignBurst,
    updateCampaignBurstObject,
    errorValidationCampaignBurstGoal,
    setErrorValidationCampaignBurstGoal,
    readOnly,
    campaignBurstNameStatus,
    setCampaignBurstNameStatus,
    staticData,
    IsValidBurstName,
    ValidationInputCampaignGoal
}) => {
    const minLengthToStartSearch = 3;
    const [usedBudget, setUsedBudget] = useState();
    const [validationResults, setValidationResults] = useState({
        name: DefaultInputStatus(),
        budget: false,
        startDate: false,
        endDate: false,
        campaignId: false
    });

    const [nameTracking, setNameTracking] = useState({
        changed: false,
        focused: false
    });

    const validateBudget = (budget, availableBudget) => {
        if (budget && availableBudget && parseInt(budget) <= parseInt(availableBudget)) {
            return true;
        }
        return false;
    };

    const validateDates = (startDate, endDate) => {
        const eDate = new Date(endDate);
        const sDate = new Date(startDate);
        if (sDate && sDate <= eDate) {
            return true;
        } else {
            return false;
        }
    };

    const initialiseDayParts = () => {
        const newDps = [];
        newDps.push({
            id: window.emptyGuid,
            name: window.translate('All Dayparts')
        });
        newDps.push({ id: '', name: '-' });

        for (let i = 0; i < campaignBurst.applicableDayParts.length; i++) {
            const dp = campaignBurst.applicableDayParts[i];
            newDps.push(dp);
            dp.children = [];
            dp.qtrs = [];
            const startHour = dp.fromTime / 3600;
            const endHour = dp.toTime / 3600;
            const startQtr = dp.fromTime / 900;
            const endQtr = dp.toTime / 900;

            for (let j = startQtr; j < endQtr; j++) {
                dp.qtrs.push(j);
            }

            for (let j = startHour; j < endHour; j++) {
                const jpad = j < 10 ? '0' + j : j;
                const j1 = j + 1;
                const jpad1 = j1 < 10 ? '0' + j1 : j1;

                const hour = {
                    id: 'h' + j,
                    name: jpad + ':00-' + jpad1 + ':00',
                    children: [
                        {
                            id: j + ':00',
                            name: jpad + ':00-' + jpad + ':15'
                        },
                        {
                            id: j + ':15',
                            name: jpad + ':15-' + jpad + ':30'
                        },
                        {
                            id: j + ':30',
                            name: jpad + ':30-' + jpad + ':45'
                        },
                        {
                            id: j + ':45',
                            name: jpad + ':45-' + jpad1 + ':00'
                        }
                    ],
                    qtrs: [j * 4 + 0, j * 4 + 1, j * 4 + 2, j * 4 + 3]
                };
                dp.children.push(hour);
            }
        }

        campaignBurst.dayParts = newDps;
    };

    useEffect(() => {
        if (campaignBurst.campaignId !== window.emptyGuid && parentCampaign.id !== campaignBurst.campaignId) {
            typeof loadParentCampaign === 'function' && loadParentCampaign(campaignBurst.campaignId);
        }
    }, [campaignBurst.campaignId]);

    useEffect(() => {
        if (!campaignBurst.copyTypes || campaignBurst.copyTypes.length === 0) {
            const standardScript = staticData?.scriptFactors?.find((s) => {
                return s.scriptLength === 30 && !s.scriptLengthFirst && !s.scriptLengthLast;
            });

            const defaultSpotType = staticData?.spotTypes?.find((s) => s.isDefault);

            if (standardScript && defaultSpotType) {
                const newCopyType = {
                    campaignId: campaignBurst.id,
                    spotTypeId: defaultSpotType.id,
                    scriptFactorId: standardScript.id,
                    ratio: 100
                };
                campaignBurst.copyTypes = [newCopyType];
            }
        }
        if (!campaignBurst.applicableDayParts) {
            GetAll('DaypartSet/ApplicableDayPartSet').then((data) => {
                campaignBurst.applicableDayParts = data;

                initialiseDayParts();
            });
        } else {
            initialiseDayParts();
        }
    }, [campaignBurst.id, campaignBurst.applicableDayParts, campaignBurst.copyTypes]);

    useEffect(() => {
        if (
            parentCampaign.id !== undefined &&
            parentCampaign.id !== window.emptyGuid &&
            campaignBurst.budget !== undefined
        ) {
            setValidationResults((validationResults) => {
                return { ...validationResults, campaignId: true };
            });

            GetCampaignBursts(parentCampaign?.id).then((data) => {
                setUsedBudget(
                    data.reduce((a, b) => {
                        // Don't include current burst budget as we want this to display in total available
                        if (b.id === campaignBurst.id) {
                            return a;
                        }

                        return a + b.spendDiscounted;
                    }, 0)
                );
            });
            const goalBudgetValue = campaignBurst.campaignGoal.find((s) => s.goal === 'budget');
            goalBudgetValue.value = campaignBurst?.budget;
            updateCampaignGoalBurst(goalBudgetValue);
        }
    }, [parentCampaign?.id, campaignBurst?.budget]);

    useEffect(() => {
        if (campaignBurst.budget && parentCampaign.campaignGoal) {
            setValidationResults((validationResults) => {
                return {
                    ...validationResults,
                    budget: validateBudget(
                        campaignBurst.budget,
                        (parentCampaign?.campaignGoal?.find(
                            (s) => s.goal.toLowerCase() === CampaignGoalEnum.BUDGET.toLowerCase()
                        ).value ?? 0) - usedBudget
                    )
                };
            });
        }
    }, [campaignBurst.budget, usedBudget]);

    useEffect(() => {
        if (campaignBurst.startDate && campaignBurst.endDate) {
            const result = validateDates(campaignBurst.startDate, campaignBurst.endDate);
            setValidationResults((validationResults) => {
                return { ...validationResults, startDate: result, endDate: result };
            });
        } else {
            setValidationResults((validationResults) => {
                return { ...validationResults, startDate: false, endDate: false };
            });
        }
    }, [campaignBurst.startDate, campaignBurst.endDate]);

    useEffect(() => {
        if (nameTracking.focused) {
            validateBurstName(
                parentCampaign.id,
                campaignBurst.id,
                campaignBurst.name,
                minLengthToStartSearch,
                true
            ).then((results) => {
                setValidationResults({ ...validationResults, name: results });
                setCampaignBurstNameStatus(results.status);
                IsValidBurstName(!results.status?.statusMessage);
            });
        }
    }, [campaignBurst.name]);

    function changeBurstName(key, value) {
        setNameTracking({ ...nameTracking, changed: true });
        updateCampaignBurstObject(key, value);
    }

    function focusBurstName(val) {
        setNameTracking({ ...nameTracking, focused: val });
    }

    const updateCampaignGoalBurst = (item) => {
        if (item.goal === 'budget') {
            campaignBurst.budget = item.value;
        }
        const copy = [...campaignBurst.campaignGoal];

        const goalBurstToUpdate = copy.find((g) => g.goal === item.goal);
        goalBurstToUpdate.selected = item.selected;
        goalBurstToUpdate.value = item.value;
        goalBurstToUpdate.values = item.values;

        updateCampaignBurstObject('campaignGoal', copy);
    };

    const handleCampaignGoalBurstChange = () => {
        try {
            let message = '';

            if (
                campaignBurst.campaignGoal.find((item) => item.selected === true).goal.toLowerCase() ===
                    CampaignGoalEnum.BUDGET.toLowerCase() ||
                campaignBurst.campaignGoal.find((item) => item.selected === true).goal.toLowerCase() ===
                    CampaignGoalEnum.WEEKLYBUDGET.toLowerCase()
            ) {
                if (
                    !validateBudget(
                        campaignBurst.budget,
                        (parentCampaign?.campaignGoal?.find(
                            (s) => s.goal.toLowerCase() === CampaignGoalEnum.BUDGET.toLowerCase()
                        ).value ?? 0) - usedBudget
                    )
                ) {
                    message = 'Budget cannot exceed available value';
                }
            }

            setErrorValidationCampaignBurstGoal(message);
        } catch (err) {
            console.log(err);
        }
    };

    return (
        <div style={pageContainerStyle}>
            <CampaignBurstSummary
                campaignBurst={campaignBurst}
                updateCampaignBurst={updateCampaignBurstObject}
                validationResults={validationResults}
                parentCampaign={parentCampaign}
                changeBurstName={changeBurstName}
                focusBurstName={focusBurstName}
                readOnly={readOnly}
                campaignBurstNameStatus={campaignBurstNameStatus}
                setCampaignBurstNameStatus={setCampaignBurstNameStatus}
            />
            <Spacer height={16} width={0} />
            <CampaignBurstCommercials
                parentCampaign={parentCampaign}
                usedBudget={usedBudget}
                campaignBurst={campaignBurst}
                updateCampaignBurst={updateCampaignBurstObject}
                setValidJcn={setValidJcn}
                validJcn={validJcn}
                readOnly={readOnly}
            />
            <Spacer height={16} width={0} />
            <PlanningGoals
                campaign={campaignBurst}
                budget={campaignBurst?.budget}
                data={goalUIStructure(campaignBurst?.campaignGoal)}
                updateCampaignGoal={updateCampaignGoalBurst}
                onChange={handleCampaignGoalBurstChange}
                readOnly={readOnly}
                errorValidationCampaignGoal={errorValidationCampaignBurstGoal}
                setErrorValidationCampaignGoal={setErrorValidationCampaignBurstGoal}
                isBurst={true}
                validationInputCampaignGoal={ValidationInputCampaignGoal}
            />
            <Spacer height={16} width={0} />
            <CampaignBurstSavePanel
                campaignBurst={campaignBurst}
                validationResults={validationResults}
                createCampaignBurst={createCampaignBurst}
                updateCampaignBurst={updateCampaignBurst}
            />
        </div>
    );
};

export default CampaignBurstBrief;
